import React, { useState, useRef } from "react";
import { toast } from "react-toastify";
import { C } from "../constants";
import { IPD_COLORS } from "../../ipd/utils/ipdColors";
import IpdButton from "../../ipd/utils/IpdButton";
import SvgIcon from "../SvgIcon";
import { toBillingError } from "../billing-api";
import { getImportTemplate, importServices } from "../service-master-api";

export default function ServiceMasterImportModal({ onClose, onImported }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [createMissingMasters, setCreateMissingMasters] = useState(true);
  const [busy, setBusy] = useState("");
  const [result, setResult] = useState(null);
  const [validated, setValidated] = useState(false);
  const [error, setError] = useState("");

  const downloadTemplate = async () => {
    setBusy("template");
    try {
      const csv = await getImportTemplate();
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "service_master_import_template.csv";
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(toBillingError(err, "Could not download template"));
    } finally {
      setBusy("");
    }
  };

  const run = async (dryRun) => {
    if (!file) { setError("Choose a file first"); return; }
    setBusy(dryRun ? "validate" : "import"); setError("");
    try {
      const res = await importServices(file, { dryRun, createMissingMasters });
      setResult(res);
      const created = res.created_count ?? res.created?.length ?? 0;
      const errCount = res.error_count ?? res.errors?.length ?? 0;
      if (dryRun) {
        setValidated(true);
        toast.info(`Validated: ${created} ready${errCount ? `, ${errCount} error(s)` : ""}`);
      } else {
        if (errCount) toast.warning(`Imported ${created} row(s), ${errCount} error(s)`);
        else toast.success(`Imported ${created} row(s)`);
        await onImported();
      }
    } catch (err) {
      setError(toBillingError(err, dryRun ? "Validation failed" : "Import failed"));
    } finally {
      setBusy("");
    }
  };

  const onPickFile = (e) => {
    setFile(e.target.files?.[0] || null);
    setResult(null);
    setValidated(false);
    setError("");
  };

  return (
    // Overlay rgba(13,35,63,0.45) has no IPD_COLORS.shadow equivalent — left as-is.
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(13,35,63,0.45)", zIndex: 300, display: "flex", alignItems: "center", justifyContent: "center", padding: "20px" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ width: "560px", maxWidth: "100%", maxHeight: "90vh", overflowY: "auto", background: IPD_COLORS.cardBg, borderRadius: "12px" }}>
        <div style={{ padding: "18px 22px", borderBottom: `1px solid ${C.border}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <span style={{ fontSize: "16px", fontWeight: 800, color: C.text }}>Import services (CSV)</span>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer" }}>
            <SvgIcon name="x" size={18} color={C.textSub} />
          </button>
        </div>

        <div style={{ padding: "20px 22px", display: "flex", flexDirection: "column", gap: "16px" }}>
          <div>
            <div style={{ fontSize: "12px", fontWeight: 700, color: C.textSub, textTransform: "uppercase", marginBottom: "8px" }}>1. Download template</div>
            <IpdButton variant="outlined" onClick={downloadTemplate} loading={busy === "template"} startIcon={<SvgIcon name="download" size={15} color={C.text} />}>
              {busy === "template" ? "Preparing…" : "Download template"}
            </IpdButton>
            <div style={{ fontSize: "11px", color: C.textSub, marginTop: "6px", fontFamily: "monospace" }}>
              unique_code,name,group_code,classification_code,fee,bed_type_code,sample_code,
              service_type,unit,charge_modifiable,quantity_modifiable,description
            </div>
          </div>

          <div>
            <div style={{ fontSize: "12px", fontWeight: 700, color: C.textSub, textTransform: "uppercase", marginBottom: "8px" }}>2. Upload file</div>
            <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={onPickFile} style={{ fontSize: "13px", color: C.text }} />
            <label style={{ display: "flex", alignItems: "center", gap: "8px", fontSize: "13px", color: C.text, marginTop: "10px", cursor: "pointer" }}>
              <input type="checkbox" checked={createMissingMasters} onChange={(e) => { setCreateMissingMasters(e.target.checked); setValidated(false); }} />
              Create missing groups / classifications / sample types from CSV codes
            </label>
          </div>

          <div style={{ display: "flex", gap: "10px" }}>
            <IpdButton variant="outlined" onClick={() => run(true)} disabled={!file || Boolean(busy)} loading={busy === "validate"}>
              Validate (dry run)
            </IpdButton>
            <IpdButton onClick={() => run(false)} disabled={!file || Boolean(busy) || !validated} loading={busy === "import"}>
              Confirm import
            </IpdButton>
          </div>
          {!validated && file && <div style={{ fontSize: "11px", color: C.textSub }}>Run a dry-run validation first, then confirm the import.</div>}

          {error && (
            <div style={{ padding: "10px 14px", background: IPD_COLORS.error.bg, border: `1px solid ${IPD_COLORS.error.border}`, borderRadius: "8px", color: IPD_COLORS.error.text, fontSize: "12px", fontWeight: 600 }}>
              {error}
            </div>
          )}

          {result && (
            <div style={{ padding: "14px 16px", background: IPD_COLORS.pageBg, border: `1px solid ${C.border}`, borderRadius: "10px" }}>
              <div style={{ fontSize: "13px", fontWeight: 700, color: C.text, marginBottom: "8px" }}>
                {result.dry_run !== false && !result.created_count ? "Preview" : "Result"}
              </div>
              <div style={{ display: "flex", gap: "18px", fontSize: "13px", color: C.textSub, flexWrap: "wrap" }}>
                <span>Rows: <b style={{ color: C.text }}>{result.total_rows ?? "—"}</b></span>
                <span>Ready/created: <b style={{ color: C.success }}>{result.created_count ?? result.created?.length ?? 0}</b></span>
                <span>Errors: <b style={{ color: (result.error_count ?? result.errors?.length ?? 0) ? C.danger : C.text }}>{result.error_count ?? result.errors?.length ?? 0}</b></span>
              </div>
              {Array.isArray(result.errors) && result.errors.length > 0 && (
                <div style={{ marginTop: "10px", maxHeight: "160px", overflowY: "auto" }}>
                  {result.errors.map((e, i) => (
                    <div key={i} style={{ fontSize: "12px", color: C.danger, padding: "3px 0" }}>
                      Row {e.row ?? "?"}: {e.message || e.error || "error"}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <div style={{ padding: "14px 22px", borderTop: `1px solid ${C.border}`, display: "flex", justifyContent: "flex-end" }}>
          <IpdButton variant="outlined" onClick={onClose}>Close</IpdButton>
        </div>
      </div>
    </div>
  );
}
